import { useEffect, useState } from "react";

import { Button } from "@/components/ui/button";
import { useAppFeatures } from "@/hooks/useAppFeatures";
import { CountryCode } from "@/types/common";
import { PoiBrandGroupedByCategory } from "@/types/poiParameter";
import { AdminAreaSettings } from "@/types/spatial";

import { AdminAreaCard } from "./AdminAreaCard";
import { CategorySelectedList } from "./CategorySelectedList";
import { PoiAdminAreaPanel } from "./PoiAdminAreaPanel";

interface PoiSidebarProps {
  selectedBrands: PoiBrandGroupedByCategory;
  sidebarExpandedCategories: Record<string, boolean>;
  toggleSidebarExpand: (key: string) => void;
  handleDeleteCategory: (key: string) => void;
  handleDeleteBrand: (key: string, itemId: string) => void;
  onContinue: () => void;
  // Admin area props (optional for flexibility)
  onSaveAdminArea?: (params: {
    settings: AdminAreaSettings | null;
    selectedIds: string[];
  }) => void;
  onDeleteAdminArea?: () => void;
  onSwitchOverrideGlobalFilter?: (override: boolean) => void;
  isGlobalFilterActive?: boolean;
  isOverrideGlobalFilter?: boolean;
  selectedCountry: { label: string; value: CountryCode } | null;
  selectedAdminArea: AdminAreaSettings | null;
  selectedAdminAreaIds: string[];
  isContinueDisabled?: boolean;
  selectedCategories: Set<string>;
  isLoading?: boolean;
}

export const PoiSidebar = ({
  selectedCountry,
  selectedAdminArea,
  selectedAdminAreaIds,
  selectedBrands,
  sidebarExpandedCategories,
  toggleSidebarExpand,
  handleDeleteCategory,
  handleDeleteBrand,
  onContinue,
  onSaveAdminArea,
  onDeleteAdminArea,
  onSwitchOverrideGlobalFilter,
  isGlobalFilterActive,
  isOverrideGlobalFilter,
  isContinueDisabled,
  selectedCategories,
  isLoading
}: PoiSidebarProps) => {
  const { isAdminTreeEnabled } = useAppFeatures();

  const [viewMode, setViewMode] = useState<"default" | "admin-area">("default");

  // Local state for admin area editing
  const [localAdmAreaSetting, setLocalAdmAreaSetting] =
    useState<AdminAreaSettings | null>(selectedAdminArea);
  const [localAdmAreaIds, setLocalAdmAreaIds] =
    useState<string[]>(selectedAdminAreaIds);

  // sync local admin area state when entering admin area view
  useEffect(() => {
    if (viewMode === "admin-area") {
      setLocalAdmAreaSetting(selectedAdminArea);
      setLocalAdmAreaIds(selectedAdminAreaIds);
    }
  }, [selectedAdminArea, selectedAdminAreaIds, viewMode]);

  const onSaveSetting = () => {
    onSaveAdminArea?.({
      settings: localAdmAreaSetting,
      selectedIds: isAdminTreeEnabled ? localAdmAreaIds : []
    });

    setViewMode("default");
  };

  const handleDeleteAdminArea = () => {
    onDeleteAdminArea?.();
  };

  const isSaveDisabled =
    !localAdmAreaSetting?.lv1 && localAdmAreaIds.length === 0;

  const getTotalCount = () => {
    if (isAdminTreeEnabled && selectedAdminAreaIds.length > 0) {
      return selectedAdminAreaIds.length;
    } else if (selectedAdminArea?.lv0) {
      return 1;
    }
    return 0;
  };

  return (
    <div className="w-[400px] shrink-0 flex flex-col gap-4 min-h-0 overflow-hidden">
      {viewMode === "default" ? (
        <>
          <AdminAreaCard
            onClick={() => {
              setViewMode("admin-area");
              setLocalAdmAreaIds(selectedAdminAreaIds);
              setLocalAdmAreaSetting(selectedAdminArea);
            }}
            onDelete={handleDeleteAdminArea}
            selectedCount={getTotalCount()}
            onSwitchChange={onSwitchOverrideGlobalFilter}
            checked={isOverrideGlobalFilter}
            isGlobalFilterActive={isGlobalFilterActive}
          />
          <CategorySelectedList
            selectedBrands={selectedBrands}
            sidebarExpandedCategories={sidebarExpandedCategories}
            selectedCategories={selectedCategories}
            onToggleExpand={toggleSidebarExpand}
            onDeleteAllInCategory={handleDeleteCategory}
            onDeleteSingleItem={handleDeleteBrand}
            onContinue={onContinue}
            isContinueDisabled={isContinueDisabled}
            isLoading={isLoading}
          />
        </>
      ) : (
        <>
          <PoiAdminAreaPanel
            onBack={() => {
              setViewMode("default");
              setLocalAdmAreaIds([]);
              setLocalAdmAreaSetting(null);
            }}
            localAdmAreaSetting={{
              ...localAdmAreaSetting,
              lv0: selectedCountry
            }}
            setLocalAdmAreaSetting={setLocalAdmAreaSetting}
            localAdmAreaIds={localAdmAreaIds}
            setLocalAdmAreaIds={setLocalAdmAreaIds}
          />
          {/* Footer: Save Button for Admin Area */}
          <div>
            <Button
              className="w-full py-3 rounded-lg text-sm font-semibold bg-teal-500 hover:bg-teal-600 border-none disabled:bg-gainsboro disabled:text-white"
              onClick={onSaveSetting}
              disabled={isSaveDisabled}
            >
              Save
            </Button>
          </div>
        </>
      )}
    </div>
  );
};
